<template>
  <div>
    <div class="row mb-3">
      <div class="col-12" style="display: flex; align-items: center">
        <span class="display-4">
          {{ cafeteria.name }} - {{ $t("CAFETERIAS.PRODUCIBLE_PRODUCTS") }}
        </span>
        <base-button
          v-if="$currentUserCan($permissions.PERM_EDIT_CAFETERIAS) && warehouseId"
          type="button"
          class="btn btn-sm btn-primary"
          @click="openAddModal"
        >
          <span class="btn-inner--icon"><i class="fas fa-plus"></i></span>
          {{ $t("CAFETERIAS.ADD_PRODUCIBLE_PRODUCTS") }}
        </base-button>
      </div>
      <div class="col-12 mt-2">
        <p class="text-muted mb-0">
          {{ $t("CAFETERIAS.PRODUCIBLE_PRODUCTS_EXPLANATION") }}
        </p>
      </div>
    </div>

    <el-alert
      v-if="!warehouseId"
      type="warning"
      :closable="false"
      show-icon
      :title="$t('CAFETERIAS.PRODUCIBLE_PRODUCTS_NO_WAREHOUSE')"
    />

    <el-table
      v-if="warehouseId"
      class="table-responsive align-items-center table-flush"
      header-row-class-name="thead-light"
      :data="producibleProducts"
      :empty-text="$t('CAFETERIAS.PRODUCIBLE_PRODUCTS_EMPTY')"
    >
      <el-table-column
        :label="$t('COMMON.NAME')"
        prop="name"
        sortable
        min-width="220"
      />
      <el-table-column :label="$t('COMMON.SKU')" prop="sku" min-width="120" />
      <el-table-column align="right" min-width="120">
        <template v-slot="{ row }">
          <el-tooltip
            v-if="$currentUserCan($permissions.PERM_VIEW_PRODUCTS)"
            :content="$t('COMMON.VIEW')"
            placement="top"
          >
            <a
              type="text"
              class="table-action"
              style="cursor: pointer"
              @click="viewProduct(row)"
            >
              <i class="fas fa-eye"></i>
            </a>
          </el-tooltip>
          <el-tooltip
            v-if="$currentUserCan($permissions.PERM_EDIT_CAFETERIAS)"
            :content="$t('COMMON.DELETE')"
            placement="top"
          >
            <a
              type="text"
              class="table-action table-action-delete"
              style="cursor: pointer"
              @click="removeProduct(row)"
            >
              <i class="fas fa-trash"></i>
            </a>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <modal
      :show.sync="productAddModalOpened"
      modal-classes="modal-secondary"
      size="lg"
    >
      <form class="" @submit.prevent="">
        <div>
          <base-input :label="$t('CAFETERIAS.ADD_PRODUCIBLE_PRODUCTS')">
            <products-selector
              :key="selectorKey"
              :showAll="false"
              :filterOrganization="cafeteria.organization.id"
              :filterWarehouse="warehouseId"
              :filterCanBeSold="true"
              :filterIdsNotIn="producibleProductIds"
              @productChanged="
                (products) => {
                  selectedProducts = products;
                }
              "
            />
          </base-input>
        </div>
      </form>

      <template slot="footer">
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading || selectedProducts.length === 0"
          @click="addProducts"
        >
          <span v-if="loading" class="btn-inner--icon">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
          {{ $t("CAFETERIAS.ADD_PRODUCIBLE_PRODUCTS") }}
        </button>

        <button
          type="button"
          class="btn btn-link ml-auto"
          :disabled="loading"
          @click="
            () => {
              productAddModalOpened = false;
            }
          "
        >
          {{ $t("COMMON.CLOSE") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Alert, Table, TableColumn, Tooltip } from "element-ui";
import swal from "sweetalert2";
import { cloneDeep } from "lodash";
import ProductsSelector from "@/components/ProductsSelector.vue";

export default {
  name: "cafeteria-view-producible-products",

  components: {
    [Alert.name]: Alert,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tooltip.name]: Tooltip,
    ProductsSelector,
  },

  props: ["cafeteria"],

  data() {
    return {
      productAddModalOpened: false,
      selectedProducts: [],
      selectorKey: 0,
      loading: false,
    };
  },

  computed: {
    producibleProducts() {
      return this.cafeteria.producibleProducts || [];
    },

    producibleProductIds() {
      return this.producibleProducts.map((product) => product.id);
    },

    warehouseId() {
      return this.cafeteria.establishment?.warehouse?.id || null;
    },
  },

  methods: {
    openAddModal() {
      this.selectedProducts = [];
      this.selectorKey += 1;
      this.productAddModalOpened = true;
    },

    async addProducts() {
      this.loading = true;
      try {
        const cafeteriaData = cloneDeep(this.cafeteria);
        cafeteriaData.producibleProducts = [
          ...this.producibleProducts.map((product) => ({
            id: product.id,
            type: "products",
          })),
          ...this.selectedProducts,
        ];
        await this.$store.dispatch("cafeterias/update", cafeteriaData);
        this.loading = false;
        this.productAddModalOpened = false;
        this.$notify({
          type: "success",
          message: this.$t("CAFETERIAS.PRODUCIBLE_PRODUCTS_UPDATED"),
        });
        this.$emit("cafeteriaUpdated", true);
      } catch (error) {
        this.$showRequestError(error);
        this.loading = false;
      }
    },

    async removeProduct(product) {
      const confirmation = await swal.fire({
        title: this.$t("CAFETERIAS.REMOVE_THIS_PRODUCIBLE_PRODUCT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (confirmation.value !== true) {
        return;
      }

      this.loading = true;
      try {
        const cafeteriaData = cloneDeep(this.cafeteria);
        cafeteriaData.producibleProducts = this.producibleProducts
          .filter((item) => item.id !== product.id)
          .map((item) => ({ id: item.id, type: "products" }));
        await this.$store.dispatch("cafeterias/update", cafeteriaData);
        this.loading = false;
        this.$notify({
          type: "success",
          message: this.$t("CAFETERIAS.PRODUCIBLE_PRODUCTS_UPDATED"),
        });
        this.$emit("cafeteriaUpdated", true);
      } catch (error) {
        this.$showRequestError(error);
        this.loading = false;
      }
    },

    viewProduct(product) {
      this.$router.push({
        name: "View Product",
        params: { id: product.id },
      });
    },
  },
};
</script>
